import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { PLACEHOLDER } from 'components/column-renderers'
import React, { SyntheticEvent, useMemo } from 'react'
import { CaregiverRow } from 'types'
import { useShowControl } from '../../../hooks/useShowControl'
import { SubscriptionCaregiverDetailsModal } from './SubscriptionCaregiverDetailsModal'

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  active: 'Paid',
  trialing: 'Trial',
  unsubscribed: 'Unsubscribed',
  canceled: 'Canceled',
  past_due: 'Past Due',
  unpaid: 'Unpaid',
  incomplete: 'Failed',
  incomplete_expired: 'Failed',
}

export const isCardPayer = (row: Pick<CaregiverRow, 'paymentMethod'>) =>
  row.paymentMethod === 'Card'

/**
 * Status text shown in the table cell. Exported so CSV exports render exactly the same value —
 * the two used to drift apart.
 */
export function getSubscriptionStatusText(row: CaregiverRow): string {
  const { userStatus, subscriptionStatus } = row

  if (isCardPayer(row) && subscriptionStatus) {
    return PAYMENT_STATUS_LABEL[subscriptionStatus] ?? subscriptionStatus
  }

  if (!isCardPayer(row)) {
    if (!subscriptionStatus) return 'Inactive'
    return userStatus === 'active' ? 'Active' : 'Inactive'
  }

  return ''
}

export function SubscriptionStatusCell({ data }: { data: CaregiverRow }) {
  const { id } = data

  const [isOpen, onOpen, onClose] = useShowControl()

  const handleOpen = (event: SyntheticEvent) => {
    event.preventDefault()
    onOpen()
  }

  const isCard = useMemo(() => isCardPayer(data), [data])

  const status = useMemo(() => getSubscriptionStatusText(data), [data])

  return (
    <div>
      {!status && PLACEHOLDER}
      {status && !isCard && <>{status}</>}
      {status && isCard && (
        <a href="" onClick={handleOpen}>
          {status}
        </a>
      )}

      {isOpen && (
        <SubscriptionCaregiverDetailsModal userId={id} isOpen={isOpen} onClose={onClose} />
      )}
    </div>
  )
}

export const createSubscriptionStatusColumn = (): ColDef => {
  return {
    headerName: 'Status',
    field: 'subscriptionStatus',
    minWidth: 200,
    sortable: false,
    cellRenderer: (props: ICellRendererParams<CaregiverRow>) => {
      if (!props.data) return PLACEHOLDER

      return <SubscriptionStatusCell data={props.data} />
    },
  }
}
